<template>
  <div>
    <div
      class="avatar"
      :class="{
        'avatar--uploadable': canUpload,
      }"
    >
      <img
        v-if="source"
        :key="source"
        :src="source"
        :alt="name ? `${name} avatar` : 'Avatar'"
        class="avatar__img"
      />
      <ClearbitLogo
        v-else
        class="avatar__img"
        :company-url="companyUrl"
        @load="onClearbitLoad"
        @error="onClearbitError"
      >
        <div
          class="avatar__initials"
          :style="{ background: avatarColor }"
        >
          {{ initials }}
        </div>
      </ClearbitLogo>

      <template v-if="canUpload">
        <input
          id="avatar-upload__input"
          ref="input"
          type="file"
          accept="image/*"
          class="avatar__input"
          :disabled="disabled"
          @change="onFile"
        />
      </template>
      <Spinner
        v-if="loading"
        class="spinner"
        size="md"
        color="dark"
        label="avatar upload"
      />
    </div>

    <PopOverMenu
      v-if="canUpload"
      placement="right-start"
    >
      <template #target>
        <div class="avatar__upload-button">
          <Icon name="mono-edit" />
        </div>
      </template>

      <template #menu>
        <List class="avatar__upload-list">
          <ListButton @click="input?.click()">
            <span class="avatar__upload-list-btn--upload">
              {{ uploadItemText }}
            </span>
          </ListButton>

          <template v-if="!noReset">
            <Divider />

            <ListButton
              class="avatar__upload-list-btn--delete"
              @click="onDelete"
            >
              {{ resetItemText }}
            </ListButton>
          </template>
        </List>
      </template>
    </PopOverMenu>
  </div>
</template>

<script lang="ts">
export default {
  name: 'AvatarUpload',
}
</script>

<script lang="ts" setup>
import md5 from 'blueimp-md5'
import { computed, ref, watch } from 'vue'

import type { AvatarUploadData } from '@/components/Common/AvatarUpload/types'
import ClearbitLogo from '@/components/Common/ClearbitLogo.vue'
import { getFileFromUrl, readFileAsBinary, readFileAsData } from '@/core/utils/file'
import Icon from '@/uiKit/Icon/Icon.vue'
import { getShortenedName } from '@/core/utils/formatter'
import List from '@/uiKit/List/List.vue'
import ListButton from '@/uiKit/List/ListButton.vue'
import Spinner from '@/uiKit/Spinner/Spinner.vue'
import { getColorHash } from '@/uiKit/colorPalette'
import Divider from '@/uiKit/Divider/Divider.vue'
import PopOverMenu from '@/uiKit/PopOverMenu.vue'

const props = withDefaults(
  defineProps<{
    src?: string | null
    name?: string
    id?: string | number
    companyUrl?: string | null
    canUpload?: boolean
    disabled?: boolean
    loading?: boolean
    uploadItemText?: string
    resetItemText?: string
    noReset?: boolean
  }>(),
  {
    src: null,
    name: '',
    id: '',
    companyUrl: 'null',
    canUpload: false,
    disabled: false,
    loading: false,
    uploadItemText: 'Upload logo',
    resetItemText: 'Reset logo',
    noReset: false,
  },
)

const emit = defineEmits<{
  (e: 'change', eventParams: AvatarUploadData | null): void
}>()

const clearbitLoaded = ref(false)
const image = ref<string | null>(null)
const input = ref<HTMLInputElement | null>(null)
const source = ref<string | null>(props.src)

watch(
  () => image.value,
  (imageValue) => {
    source.value = imageValue
  },
)

const avatarColor = computed(() => getColorHash(props.id?.toString() + props.name.trim()))
const initials = computed(() => getShortenedName(props.name))

const onClearbitLoad = async (url: string): Promise<void> => {
  clearbitLoaded.value = true
  const file = await getFileFromUrl('logo.png', url)
  const binaryContent = await readFileAsBinary(file)
  const hash = md5(binaryContent)
  const eventParams: AvatarUploadData = {
    hash,
    file,
    type: file ? file.type : null,
  }
  emit('change', eventParams)
}

const onClearbitError = (): void => {
  clearbitLoaded.value = false
  if (source.value) {
    return
  }
  emit('change', null)
}

const onFile = async (event: Event): Promise<void> => {
  // event.target is not generic and cannot be properly set to input element
  // so this is as safe a cast as it can be
  const input = event.target ? (event.target as HTMLInputElement) : null
  const files = input?.files

  if (!files || files.length === 0) {
    return
  }
  const file = files[0]
  const [loadedImage, binaryContent] = await Promise.all([
    readFileAsData(file),
    readFileAsBinary(file),
  ])

  image.value = loadedImage

  const hash = md5(binaryContent)
  const eventParams: AvatarUploadData = {
    hash,
    file,
    type: file ? file.type : null,
  }
  emit('change', eventParams)
}

const onDelete = (): void => {
  image.value = ''

  if (input.value) {
    input.value.value = ''
  }

  emit('change', null)
}
</script>

<style lang="scss" scoped>
@import '@/uiKit/assets/index.scss';
$size: 74px;
.spinner {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  margin: auto;
}
.avatar {
  position: relative;
  @include col--center;
  width: $size;
  height: $size;
  min-width: $size;
  min-height: $size;
  border-radius: 12px;
  overflow: hidden;
  background: #e9eaec;
  transition: all 0.2s ease-in-out;
}

.avatar__img {
  width: 100%;
  height: 100%;
  border-radius: 12px;
  object-fit: cover;
  overflow: hidden;
}

.avatar__initials {
  position: absolute;
  @include fullsize;
  @include row--center;
  @include typography(lg, inter);
  border-radius: 12px;
  color: var(--color-text-default);
  text-align: center;
  @include noSelect;
}

.avatar__input {
  pointer-events: none;
  position: absolute;
  @include fullsize;
  border-radius: 12px;
  opacity: 0;
}

.avatar__upload-button {
  position: relative;
  width: 32px;
  bottom: 25px;
  left: 45px;
}
.avatar__upload-button {
  transition-property: background-color;
  transition: $transitionDefault;
  display: inline-flex;
  align-items: center;
  flex-direction: row;
  justify-content: center;
  gap: 8px;
  cursor: pointer;

  border-radius: 100px;

  height: 32px;
  width: 32px;
  padding: 6px;

  border: 1px solid var(--color-border-default);
  color: var(--color-text-default);

  background-color: var(--color-surface-default);

  &:hover {
    background: var(--color-surface-tertiary);
    border-color: var(--color-border-input-hovered);
  }

  &:disabled {
    opacity: 0.5;
  }
}
.avatar__upload-list {
  width: 220px;
}
.avatar__upload-list-btn--upload {
  cursor: pointer;
  color: var(--color-text-default);
}
.avatar__upload-list-btn--delete {
  color: var(--color-text-critical);
}
</style>
